import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Truck, MapPin, Loader2, Send, Crosshair } from "lucide-react";
import { toast } from "sonner";

/**
 * BusinessLocationPingCard — for food trucks / pop-up shops.
 *
 * Toggle `is_mobile` on the business profile to unlock the ping composer.
 * Pinging sends a message broadcast to every opted-in customer matching the
 * business's category preferences. Bypasses the 24h promo cooldown but still
 * respects category + geo filters.
 */
export default function BusinessLocationPingCard({ marketingActive, subscriptionActive }) {
  const { user, refresh } = useAuth();
  const [isMobile, setIsMobile] = useState(!!user?.is_mobile);
  const [savingToggle, setSavingToggle] = useState(false);
  const [label, setLabel] = useState("");
  const [zipsRaw, setZipsRaw] = useState("");
  const [sending, setSending] = useState(false);
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [detecting, setDetecting] = useState(false);
  const [lastPing, setLastPing] = useState(null);

  useEffect(() => setIsMobile(!!user?.is_mobile), [user?.is_mobile]);
  const canConfigure = subscriptionActive && marketingActive;

  useEffect(() => {
    if (!isMobile || !canConfigure) return;
    api.get("/me/business/location-pings")
      .then(({ data }) => setLastPing((data?.pings || [])[0] || null))
      .catch(() => {});
  }, [isMobile, canConfigure]);

  const saveToggle = async (next) => {
    setSavingToggle(true);
    setIsMobile(next);
    try {
      await api.put("/me/business", { is_mobile: next });
      toast.success(next ? "Mobile mode enabled" : "Mobile mode disabled");
      if (refresh) refresh();
    } catch (err) {
      setIsMobile(!next);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingToggle(false);
    }
  };

  const detectLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Your browser can't share GPS coordinates");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCoords({ lat: p.coords.latitude, lng: p.coords.longitude });
        toast.success(`GPS locked (${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)})`);
        setDetecting(false);
      },
      (err) => {
        toast.error(`Location denied: ${err.message}`);
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const sendPing = async () => {
    const text = label.trim();
    if (text.length < 2) {
      toast.error("Add a short location label (e.g. '2nd & Elm — until 8pm')");
      return;
    }
    setSending(true);
    try {
      const zips = zipsRaw
        .split(/[,\s]+/)
        .map((z) => z.trim())
        .filter((z) => /^\d{5}$/.test(z));
      const { data } = await api.post("/me/business/location-ping", {
        label: text,
        lat: coords.lat,
        lng: coords.lng,
        zips: zips.length ? zips : undefined,
      });
      toast.success(`Ping sent to ${data.recipients} customer${data.recipients === 1 ? "" : "s"}`);
      setLastPing(data.ping);
      setLabel("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="rounded-md border border-border p-6" data-testid="business-location-ping-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
          <Truck size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl">Mobile business — live location</h3>
            <Badge
              variant="outline"
              className={`rounded-sm text-[10px] font-mono ${
                isMobile ? "border-emerald-400 text-emerald-700" : "border-gray-300 text-gray-500"
              }`}
            >
              {isMobile ? "MOBILE MODE" : "STATIC"}
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Food truck, pop-up, or mobile service? Enable Mobile Mode, then
            ping your current spot. Opted-in customers matching your category
            get an instant message — and the 24h promo cooldown is bypassed for
            location updates.
          </p>
        </div>
        <div className="inline-flex items-center gap-2">
          <Switch
            checked={isMobile}
            onCheckedChange={saveToggle}
            disabled={savingToggle}
            data-testid="business-mobile-toggle"
          />
          <span className="text-xs font-mono text-gray-600">Mobile</span>
        </div>
      </div>

      {!canConfigure && isMobile && (
        <p className="mt-5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
          Location pings need an active servrevu Pro subscription + the Marketing add-on
          ($35/mo) so we can send on your behalf.
        </p>
      )}

      {isMobile && canConfigure && (
        <>
          <div className="mt-5 space-y-4">
            <div>
              <Label htmlFor="ping-label" className="text-xs font-mono uppercase tracking-wider text-gray-600">
                Where are you right now?
              </Label>
              <Input
                id="ping-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={140}
                placeholder="2nd & Elm, parked in front of Berkeley Bowl — until 8pm"
                className="rounded-sm mt-1"
                data-testid="location-ping-label-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-gray-600">
                  GPS coordinates <span className="text-gray-400 font-normal normal-case">(optional)</span>
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={detectLocation}
                    disabled={detecting}
                    className="rounded-[35px]"
                    data-testid="location-ping-gps-button"
                  >
                    {detecting ? (
                      <><Loader2 size={13} className="mr-1.5 animate-spin" /> Detecting…</>
                    ) : (
                      <><Crosshair size={13} className="mr-1.5" /> Use my GPS</>
                    )}
                  </Button>
                  {coords.lat != null && (
                    <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200">
                      {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="ping-zips" className="text-xs font-mono uppercase tracking-wider text-gray-600">
                  Target ZIPs <span className="text-gray-400 font-normal normal-case">(optional)</span>
                </Label>
                <Input
                  id="ping-zips"
                  value={zipsRaw}
                  onChange={(e) => setZipsRaw(e.target.value)}
                  placeholder="94704, 94703"
                  className="rounded-sm mt-1 font-mono"
                  data-testid="location-ping-zips-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Comma-separated. Leave blank to hit every opted-in customer matching your category.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              onClick={sendPing}
              disabled={sending || label.trim().length < 2}
              className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="location-ping-send-button"
            >
              {sending ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Broadcasting…</>
              ) : (
                <><Send size={14} className="mr-1.5" /> Ping my location</>
              )}
            </Button>
            {lastPing && (
              <span className="text-[11px] font-mono text-gray-500 inline-flex items-center gap-1.5">
                <MapPin size={11} /> Last ping: {lastPing.label} ·{" "}
                {new Date(lastPing.created_at).toLocaleString()} · {lastPing.recipients_count ?? "?"} recipients
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
